import {
  CHANNEL_DISPLAY_ORDER,
  NOTIFICATION_CHANNELS,
  type AdminChannel,
  type AdminCommunication,
  type AdminTranslation,
  type CommunicationLocale,
  type NotificationChannel,
} from "./communications-types";

/** Find a communication by id within a list (returns null when absent). */
export function findCommunication(
  communications: readonly AdminCommunication[] | undefined,
  id: string | undefined,
): AdminCommunication | null {
  if (!communications || !id) return null;
  return communications.find((c) => c.id === id) ?? null;
}

/** Find a channel by id within a communication. */
export function findChannel(
  communication: AdminCommunication | null,
  channelId: string | undefined,
): AdminChannel | null {
  if (!communication || !channelId) return null;
  return communication.channels.find((c) => c.id === channelId) ?? null;
}

/** A channel's translations for one locale, newest version first. */
export function versionsForLocale(
  channel: AdminChannel | null,
  localeCode: string | undefined,
): readonly AdminTranslation[] {
  if (!channel || !localeCode) return [];
  return channel.translations
    .filter((tr) => tr.localeCode === localeCode)
    .slice()
    .sort((a, b) => b.version - a.version);
}

/** The active translation for a `(channel, locale)`, or null. */
export function activeTranslation(
  channel: AdminChannel | null,
  localeCode: string | undefined,
): AdminTranslation | null {
  return versionsForLocale(channel, localeCode).find((tr) => tr.isActive) ?? null;
}

/** Distinct locale codes that already have at least one translation. */
export function localesWithTranslations(channel: AdminChannel | null): readonly string[] {
  if (!channel) return [];
  const seen = new Set<string>();
  for (const tr of channel.translations) seen.add(tr.localeCode);
  return [...seen].sort();
}

/**
 * Distinct locale codes that have an ACTIVE translation on a channel, sorted.
 * Drives the test-dispatch locale select (API-COMM-5) — only locales with live
 * copy are offered so the operator dispatches something that actually renders.
 */
export function localesWithActiveTranslation(channel: AdminChannel | null): readonly string[] {
  if (!channel) return [];
  const seen = new Set<string>();
  for (const tr of channel.translations) {
    if (tr.isActive) seen.add(tr.localeCode);
  }
  return [...seen].sort();
}

/**
 * Channel display order helper — sort a communication's channels into the
 * canonical {@link CHANNEL_DISPLAY_ORDER}, with any unexpected channel
 * appended (defensive: a new engine channel shouldn't vanish from the UI).
 */
export function sortChannels(channels: readonly AdminChannel[]): readonly AdminChannel[] {
  const rank = (c: NotificationChannel): number => {
    const i = CHANNEL_DISPLAY_ORDER.indexOf(c);
    return i === -1 ? CHANNEL_DISPLAY_ORDER.length : i;
  };
  return channels.slice().sort((a, b) => rank(a.channel) - rank(b.channel));
}

/**
 * Locales available to translate into for a channel: catalog locales that
 * don't yet have a translation on this channel. Used by the "add
 * translation" affordance so the operator can't pick a locale that would
 * 409 `TRANSLATION_ALREADY_EXISTS`.
 */
export function untranslatedLocales(
  channel: AdminChannel | null,
  locales: readonly CommunicationLocale[] | undefined,
): readonly CommunicationLocale[] {
  if (!channel || !locales) return [];
  const taken = new Set(localesWithTranslations(channel));
  return locales.filter((l) => !taken.has(l.code));
}

/**
 * A communication's ACTIVE channels, in canonical display order. The
 * test-dispatch dialog (API-COMM-5) can only force an active channel — inactive
 * channels must be enabled first, so they're never offered.
 */
export function activeChannels(communication: AdminCommunication | null): readonly AdminChannel[] {
  if (!communication) return [];
  return sortChannels(communication.channels.filter((c) => c.isActive));
}

/**
 * Channel types the "add channel" control can offer for a communication: the
 * fixed type enum minus the types already present. A soft-deleted channel
 * isn't in `channels` (the list hides it), so its type reappears here and the
 * add POST revives it. For an email-only communication (critical / ADMIN) only
 * EMAIL is offerable — the API rejects any other with
 * CANNOT_ENABLE_NON_EMAIL_ON_CRITICAL.
 */
export function addableChannelTypes(
  communication: AdminCommunication | null,
): readonly NotificationChannel[] {
  if (!communication) return [];
  const present = new Set(communication.channels.map((c) => c.channel));
  const emailOnly = communication.isCritical || communication.audience === "ADMIN";
  return NOTIFICATION_CHANNELS.filter((c) => !present.has(c) && (!emailOnly || c === "EMAIL"));
}
